// libraries.
import { useState } from "react";

// store.
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";
import { useUserProfile } from "@shared/GoogleLoginButton/user-slice/useUserProfile.hook";

// styles.
import { StyledRulesTable } from "../styles";

// components.
import { EditExceptionModal } from "./EditExceptionModal";
import { ConfirmToggleModal } from "./ConfirmToggleModal";
import { CreateRuleModal } from "./CreateRuleModal";

// interfaces.
import { ExceptionRule } from "../rulesData";
import { RawRuleItem } from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

const OPERATOR_LABELS: Record<string, string> = {
  equals: "=",
  not_equals: "≠",
  greater_than: ">",
  greater_or_equal: "≥",
  less_than: "<",
  less_or_equal: "≤",
};

export const ExceptionsTableTab = () => {
  const { country, email } = useUserProfile();
  const resolvedCountry = country || "CO";
  const { exceptions, getRawRule, toggleRule, isSaving } = useRulesSlice();

  const [editingException, setEditingException] = useState<ExceptionRule | null>(null);
  const [editingRawRule, setEditingRawRule] = useState<RawRuleItem | null>(null);
  const [confirmToggleRule, setConfirmToggleRule] = useState<RawRuleItem | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const handleEdit = (exception: ExceptionRule) => {
    setEditingException(exception);
    setEditingRawRule(getRawRule(exception.id, resolvedCountry) ?? null);
  };

  const handleClose = () => {
    setEditingException(null);
    setEditingRawRule(null);
  };

  const handleToggleConfirm = () => {
    if (confirmToggleRule) toggleRule(confirmToggleRule, resolvedCountry, email);
    setConfirmToggleRule(null);
  };

  return (
    <>
      <StyledRulesTable>
        <div className="d-flex justify-content-end mb-2">
          <button className="action-btn btn-edit" onClick={() => setShowCreate(true)}>
            ＋ Nueva regla
          </button>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th style={{ width: "18%", textAlign: "left" }}>Nombre</th>
              <th style={{ width: "28%", textAlign: "left" }}>Descripción</th>
              <th style={{ width: "18%" }}>Condición</th>
              <th>Prioridad</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {exceptions.map((exception, idx) => {
              const raw = getRawRule(exception.id, resolvedCountry);
              const isActive = raw?.is_active === "active";
              const operator =
                OPERATOR_LABELS[
                  (raw?.rule.condition as { type: string })?.type ?? "equals"
                ] ?? "=";

              return (
                <tr key={exception.id}>
                  <td>
                    <div className="cell-centered" style={{ color: "#999" }}>{idx + 1}</div>
                  </td>

                  <td>
                    <span className="cell-name">{exception.name}</span>
                  </td>

                  <td>
                    <span className="cell-description">
                      {exception.description}
                    </span>
                  </td>

                  <td>
                    <div className="cell-centered">
                      <code style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {exception.field} {operator} {exception.triggerValue}
                      </code>
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered">
                      {raw?.rule.priority ?? "—"}
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered">
                      <span className={`badge-status ${isActive ? "active" : "inactive"}`}>
                        {isActive ? "● Activa" : "○ Inactiva"}
                      </span>
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered gap-2">
                      <button
                        className="action-btn btn-edit"
                        onClick={() => handleEdit(exception)}
                        title="Editar excepción"
                        disabled={isSaving}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        className={`action-btn ${isActive ? "btn-deactivate" : "btn-activate"}`}
                        onClick={() => raw && setConfirmToggleRule(raw)}
                        disabled={isSaving || !raw}
                        title={isActive ? "Desactivar regla" : "Activar regla"}
                      >
                        {isActive ? "⏸ Desactivar" : "▶ Activar"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {exceptions.length === 0 && (
          <p className="text-muted text-center mt-4">No hay excepciones cargadas.</p>
        )}
      </StyledRulesTable>

      <EditExceptionModal
        exception={editingException}
        rawRule={editingRawRule}
        country={resolvedCountry}
        onClose={handleClose}
      />

      <ConfirmToggleModal
        rawRule={confirmToggleRule}
        onConfirm={handleToggleConfirm}
        onClose={() => setConfirmToggleRule(null)}
      />

      <CreateRuleModal
        show={showCreate}
        country={resolvedCountry}
        onClose={() => setShowCreate(false)}
      />
    </>
  );
};
